import { Edit, Visibility } from '@mui/icons-material';
import { Grid, IconButton, useEventCallback } from '@mui/material';
import { memo, ReactElement, useMemo } from 'react';
import Table from 'shell/components/shared/table';
import { headCells } from './constants';
import { noop } from 'shell/utils/noop';
import {
  DeclarationsListVM,
  FilterResearchFormIcaro,
} from 'civil-status/view-models/research-form-icaro';
import { STATES_DECLARATIONS } from 'civil-status/constants/states-declarations';
import ClearIcon from '@mui/icons-material/Clear';
import PersonIcon from '@mui/icons-material/Person';
import { generatePath, useNavigate } from 'react-router-dom';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { toast } from 'react-toastify';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { AxiosResponse } from 'axios';
import { HttpResponse } from 'shell/models/http-response';

interface TableDeclarationsProps {
  data?: Array<DeclarationsListVM>;
  filterSelected?: FilterResearchFormIcaro;
  getDeclarationsListData: (data: FilterResearchFormIcaro) => void;
  tableProps?: Record<string, number>;
  setTableProps: (values: Record<string, number>) => void;
  valueCat?: string;
  idCat?: string;
}

const TableDeclarationsComponent = ({
  data,
  filterSelected,
  getDeclarationsListData,
  tableProps = {},
  setTableProps,
  idCat,
  valueCat,
}: TableDeclarationsProps): ReactElement => {
  const navigate = useNavigate();

  const onDrafting = useHttpClient('', {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const onDraftingPress = useEventCallback((item: DeclarationsListVM) => {
    if (!item.contatore) {
      return;
    }

    (
      onDrafting({
        url: `${__ACT_API_URL__}/ricerca-dichiarazione-nascita/dichiarazione/stesura/${item.contatore}`,
      }) as ResponsePromise<void>
    ).then((response) => {
      const { idEvento } = (
        response as unknown as AxiosResponse<HttpResponse<{ idEvento?: number }>>
      ).data.payload;

      toast.success('Operazione eseguita con successo');

      if (!idEvento) {
        return;
      }

      navigate({
        pathname: generatePath('/stato-civile/ricerca-atto-da-compilare/modifica/:idEvento', {
          idEvento: idEvento?.toString(),
        }),
      });
    });
  });

  const onVisibilityPress = useEventCallback((item: DeclarationsListVM) => {
    if (!item.contatore) {
      return;
    }
    navigate({
      pathname: generatePath('/stato-civile/:valueCat/:idCat/ricerca-icaro/visualizza/:contatore', {
        valueCat: valueCat || '',
        idCat: idCat || '',
        contatore: item.contatore?.toString(),
      }),
    });
  });

  const onRejectionPress = useEventCallback((item: DeclarationsListVM) => {
    if (!item.contatore) {
      return;
    }
    navigate({
      pathname: generatePath('/stato-civile/:valueCat/:idCat/ricerca-icaro/rifiuta/:contatore', {
        valueCat: valueCat || '',
        idCat: idCat || '',
        contatore: item.contatore?.toString(),
      }),
    });
  });

  const body: Array<DeclarationsListVM> = useMemo(() => {
    return (data || []).map((item) => {
      const isWithTwins = item.twins ? Number.parseInt(item.twins, 10) > 1 : false;

      return {
        ...item,
        action: (
          <div className="pmi-d-flex">
            <IconButton
              onClick={() => {
                onVisibilityPress(item);
              }}
              aria-label="Visualizza dati"
              aria-haspopup="true"
              style={{
                padding: '4px',
              }}
            >
              <Visibility />
            </IconButton>
            <IconButton
              onClick={() => {
                onDraftingPress(item);
              }}
              aria-label="Stesura dell'atto"
              aria-haspopup="true"
              style={{
                padding: '4px',
              }}
              disabled={
                isWithTwins
                  ? item.idState !== STATES_DECLARATIONS.BOZZA
                  : !(
                      item.idState === STATES_DECLARATIONS.ATTESA_STESURA ||
                      item.idState === STATES_DECLARATIONS.BOZZA
                    )
              }
            >
              <Edit />
            </IconButton>
            <IconButton
              aria-label="Registrazione in anagrafe"
              component="span"
              onClick={noop}
              disabled
            >
              <PersonIcon />
            </IconButton>
            <IconButton
              aria-label="Rifiuto dichiarazione"
              component="span"
              onClick={() => {
                onRejectionPress(item);
              }}
              disabled={
                !(
                  item.idState === STATES_DECLARATIONS.ATTESA_STESURA ||
                  item.idState === STATES_DECLARATIONS.BOZZA
                )
              }
            >
              <ClearIcon />
            </IconButton>
          </div>
        ),
      };
    });
  }, [data]);

  const onChangePage = useEventCallback((page: number) => {
    getDeclarationsListData({
      ...(filterSelected || {}),
      pageOffset: page * (filterSelected?.pageSize || 1),
    });

    setTableProps({
      page,
      rowsPerPage: tableProps.rowsPerPage,
    });
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    getDeclarationsListData({ ...(filterSelected || {}), pageSize: rowsPerPage });

    setTableProps({
      page: 0,
      rowsPerPage,
    });
  });

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Table
          ariaLabel="Lista dichiarazioni"
          page={tableProps.page || 0}
          rowsPerPage={tableProps.rowsPerPage || 10}
          body={body}
          count={(body && body[0]?.totalCount) || 0}
          head={headCells}
          onSort={noop}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
          orders={{}}
          isNotLayoutFixed
          noSort
          hideButtons
          isServer
        />
      </Grid>
    </Grid>
  );
};

TableDeclarationsComponent.displayName = 'Icaro Table component';

const TableDeclarations = memo(TableDeclarationsComponent);

export default TableDeclarations;
